import React, { FC, useCallback, useEffect, useState, useContext } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { TrainingArchiveTourSteps } from "@/features/training/pages/Archive/TrainingArchiveTourSteps";
import { ArchivedSessionsDataGrid } from "@/features/training/pages/Archive/panel-left/ArchivedSessionsDataGrid";
import KpiStatisticsDataGrid from "@/features/training/pages/Archive/panel-left/KpiStatisticsDataGrid";
import { TrainingArchiveSearch } from "@/features/training/pages/Archive/panel-left/TrainingArchiveSearch";
import { FallBackErrorMessage } from "@/features/training/ui";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { Box, Paper, Stack, Tab, Tabs, Typography, alpha } from "@mui/material";
import { useTour } from "@reactour/tour";
import * as Sentry from "@sentry/react";

import { QuickRangeToggle } from "@/components/date-range/QuickRangeToggle";
import {
	CanonicalQuickRangeKey,
	getMatchingQuickRange,
	shiftDateByQuickRange,
} from "@/components/date-range/quickRange";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


export enum TrainingType {
	All = "All",
	Courses = "Courses",
	Exercises = "Exercises",
	Events = "Events",
}

const QUICK_RANGES: readonly CanonicalQuickRangeKey[] = ["3m", "6m", "1y"];

const parseArchiveDate = (value: string | null): Date | null => {
	if (!value) return null;
	const normalized = value.replaceAll(".", "-");
	const parsed = new Date(normalized);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getQuickRangeFromDates = (
	start: Date,
	end: Date,
): CanonicalQuickRangeKey | null => {
	return getMatchingQuickRange(start, end, QUICK_RANGES) || null;
};

export const ArchivePanelLeft: FC = () => {
	const translation: Language | null = useContext(TranslationContext);
	const { setSteps, setCurrentStep } = useTour();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const [startDate, setStartDate] = useState<Date>(() => {
		return shiftDateByQuickRange("3m");
	});
	const [endDate, setEndDate] = useState<Date>(new Date());
	const [trainingType, setTrainingType] = useState<TrainingType>(
		TrainingType.All,
	);
	const [tabIndex, setTabIndex] = useState(0);
	const [selectedRange, setSelectedRange] =
		useState<CanonicalQuickRangeKey | null>("3m");

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
	};

	useEffect(() => {
		setTabIndex(pathname.includes("/training/archive/kpiParameters/") ? 1 : 0);
	}, [pathname]);

	useEffect(() => {
		const queryStartDate = parseArchiveDate(searchParams.get("StartDate"));
		const queryEndDate = parseArchiveDate(searchParams.get("EndDate"));
		if (!queryStartDate || !queryEndDate) return;

		setStartDate(queryStartDate);
		setEndDate(queryEndDate);
		setSelectedRange(getQuickRangeFromDates(queryStartDate, queryEndDate));
	}, [searchParams]);

	const applyRange = useCallback((range: CanonicalQuickRangeKey) => {
		const now = new Date();
		const start = shiftDateByQuickRange(range, now);
		setStartDate(start);
		setEndDate(now);
		setSelectedRange(range);
	}, []);

	useEffect(() => {
		setCurrentStep(0);
		if (setSteps) setSteps(TrainingArchiveTourSteps);
	}, [setSteps, setCurrentStep]);

	return (
		<Box
			data-tour="training-archive-left"
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Compact header */}
			<Box
				sx={{
					flexShrink: 0,
					py: 1.5,
					px: 0,
					ml: 1.5,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Typography variant="h6" fontWeight={700}>
					<Translate String="Training.Archive.PanelLeft.Title">
						Archived Sessions
					</Translate>
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
					<Translate String="Training.Archive.PanelLeft.Subtitle">
						Search and review past sessions. Filter by type and date range.
					</Translate>
				</Typography>
			</Box>

			{/* Filters – compact card */}
			<Box sx={{ flexShrink: 0, py: 1.5 }}>
				<Paper
					variant="outlined"
					sx={{
						p: 2,
						borderRadius: 2,
						borderColor: "divider",
						bgcolor: "background.paper",
					}}
				>
					<Stack spacing={2}>
						{/* Date range + Type – aligned in one row */}
						<TrainingArchiveSearch
							startDate={startDate}
							endDate={endDate}
							trainingType={trainingType}
							setStartDate={setStartDate}
							setEndDate={setEndDate}
							setTrainingType={setTrainingType}
						/>
						{/* Quick range – aligned with filters row */}
						<Stack
							direction="row"
							alignItems="center"
							spacing={2}
							flexWrap="wrap"
							useFlexGap
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ minWidth: 72 }}
							>
								<Translate String="Training.Archive.QuickRange">
									Quick range
								</Translate>
							</Typography>
							<QuickRangeToggle
								value={selectedRange}
								onChange={applyRange}
								options={QUICK_RANGES.map((range) => ({
									value: range,
									label: range,
								}))}
								ariaLabel="Training archive quick range"
							/>
						</Stack>
					</Stack>
				</Paper>
			</Box>

			{/* Content tabs – fills remaining space */}
			<Paper
				variant="outlined"
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					borderRadius: 2,
					borderColor: "divider",
					overflow: "hidden",
				}}
			>
				<Tabs
					value={tabIndex}
					onChange={handleTabChange}
					variant="fullWidth"
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Archive.PanelLeft.ArchivePanelLeft.L195C17.ArchiveTabs", "archive tabs")}
					sx={{
						minHeight: 40,
						borderBottom: 1,
						borderColor: "divider",
						"& .MuiTab-root": {
							textTransform: "none",
							fontWeight: 600,
							minHeight: 40,
						},
					}}
				>
					<Tab
						icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />}
						iconPosition="start"
						label={
							<Translate String="Training.Drill.Actions.ArchivedSessions">
								Archived Sessions
							</Translate>
						}
					/>
					<Tab
						icon={<InsightsOutlinedIcon sx={{ fontSize: 18 }} />}
						iconPosition="start"
						label={
							<Translate String="Training.Archive.KpiStatistics">
								KPI Statistics
							</Translate>
						}
					/>
				</Tabs>

				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						p: 2,
						"&::-webkit-scrollbar": { width: 8 },
						"&::-webkit-scrollbar-track": {
							backgroundColor: (t) =>
								alpha(
									t.palette.mode === "dark"
										? t.palette.background.default
										: t.palette.text.primary,
									t.palette.mode === "dark" ? 0.5 : 0.05,
								),
							borderRadius: 8,
						},
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: (t) =>
								alpha(
									t.palette.text.primary,
									t.palette.mode === "dark" ? 0.3 : 0.2,
								),
							borderRadius: 8,
						},
					}}
				>
					<Sentry.ErrorBoundary fallback={<FallBackErrorMessage />}>
						{tabIndex === 0 && (
							<ArchivedSessionsDataGrid
								startDate={startDate}
								endDate={endDate}
								trainingType={trainingType}
							/>
						)}
						{tabIndex === 1 && (
							<KpiStatisticsDataGrid startDate={startDate} endDate={endDate} />
						)}
					</Sentry.ErrorBoundary>
				</Box>
			</Paper>
		</Box>
	);
};
